import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzurePostgreSqlLinkedService } from 'app/model/linkedservice/azure-db-postgre.linkedservice';
import { Constants } from 'app/shared/shared-constants';
import {
    akvToResource,
    akvToModel,
    secretBaseToPasswordOrAKV,
    parameterizableSecretBaseToModel,
    parameterizablePasswordOrAKVtoSecretBase
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import {
    EncryptionMethod,
    PostGreSqlAdditionalPropertiesKeyType,
    additionalFieldsKeyOptions,
    convertToEncryptionMethod,
    convertToValidateServerCertificate
} from 'app/registries/consts/postgre-consts';
import { isString } from 'app/shared/utils/string.util';
import {
    connectionStringPartToResource,
    connectionStringPartToModel
} from 'app/shared/utils/linkedservice-parameter.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AdapterContext, Credential, azureDefaultAuthRefTypes, azurePostgreSqlAuthTypes, ITridentResource, ReferenceAdapter, ParameterConfig, ISelectOption } from 'shell-models-lib';
import { driverVersions } from 'app/registries/consts/tabular-consts';
import { ValueAdapter } from '../value.adapter';
import { servicePrincipalCredentialTypes } from 'app/model/linkedservice/dynamics.linkedservice';
import { toParameterClass, toParameterObject } from './parameter-config.util';

export type AzurePostGreSqlAdditionalPropertiesKeyType = Exclude<PostGreSqlAdditionalPropertiesKeyType, 'sslPassword'>;
export const azPgSqlAdditionalFieldsKeyOptions: Array<ISelectOption<AzurePostGreSqlAdditionalPropertiesKeyType>> =
    additionalFieldsKeyOptions.filter((option) => option.value !== 'sslPassword') as Array<ISelectOption<AzurePostGreSqlAdditionalPropertiesKeyType>>;
const azPgSqlAdditionalFieldsAllowedKeys: AzurePostGreSqlAdditionalPropertiesKeyType[] = azPgSqlAdditionalFieldsKeyOptions.map((option) => option.value);

const azPgSqlTypePropertiesAdapts = {
    server: '*',
    port: '*',
    database: '*',
    username: '*',
    sslMode: '*',

    // additional fields
    schema: '*',
    pooling: '*',
    connectionTimeout: '*',
    commandTimeout: '*',
    trustServerCertificate: '*',
    sslCertificate: '*',
    sslKey: '*',
    sslPassword: '*',
    readBufferSize: '*',
    logParameters: '*',
    timezone: '*',
    encoding: '*'
};

const azPgSqlTypePropertiesAdaptsWithAuth = {
    ...azPgSqlTypePropertiesAdapts,
    authenticationType: '*',
    tenant: '*',
    servicePrincipalId: '*',
    azureCloudType: '*',
    credential: '*',
    servicePrincipalCredentialType: '*',
    servicePrincipalKey: 'secret',
    servicePrincipalEmbeddedCert: 'secret',
    servicePrincipalEmbeddedCertPassword: 'secret'
}

/**
 * Exported so PostgreSQL wire-compatible connectors (e.g. Azure HorizonDB) can reuse the exact
 * same serialization spec when registering their own adapter.
 */
export const azurePostgreSqlLinkedServiceAdapts = {
    name: '*',
    properties: {
        version: '*',
        description: '*',
        type: '*',
        connectVia: '*',
        typeProperties: async (context: AdapterContext) => {
            const typePropertiesAdaptes = {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: AzurePostgreSqlLinkedService) => {
                    // if password is in AKV, then connectionString won't contain secret
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV && ls.password instanceof AzureKeyVault) {
                        return '*';
                    }
                    return 'secret';
                },
                password: 'secret',
                encryptedCredential: '*'
            };

            return {
                ...typePropertiesAdaptes,
                ...azPgSqlTypePropertiesAdaptsWithAuth
            };
        },
        parameters: '*'
    }
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzurePostgreSqlLinkedService),
    asynchronous: true,
    adapts: azurePostgreSqlLinkedServiceAdapts
})
export class AzurePostgreSqlLinkedServiceAdapter extends LinkedServiceAdapter<AzurePostgreSqlLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => AzurePostgreSqlLinkedService {
        return AzurePostgreSqlLinkedService;
    }

    protected model(): IModel {
        return AzurePostgreSqlLinkedService.MODEL;
    }

    protected override toAzureTypeProperties = (tridentResource: ITridentResource) => {
        try {
            const builder = new DbConnectionStringBuilderService(true);
            const connectionDetails = JSON.parse(tridentResource.connectionDetails);
            const [host, database] = connectionDetails.extensionDataSourcePath.split(';');
            builder.add('Host', host);
            builder.add('Database', database);
            const encrypted: boolean = tridentResource.credentialDetails.encryptedConnection === 'Encrypted';
            builder.add('EncryptionMethod', encrypted ? '1' : '0');
            if (encrypted) {
                builder.add('ValidateServerCertificate', '0');
            }
            return { connectionString: this._connectionStringToResource(builder.connectionString) };
        } catch (e) {
            return {};
        }
    };

    protected async propertiesToResourceAsync(
        linkedService: AzurePostgreSqlLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzurePostgreSqlLinkedService;
        properties.type = Metamodel.modelFor(AzurePostgreSqlLinkedService).name;
        if (linkedService.isOldResource) {
            properties.version = linkedService.version === driverVersions.v2 ? '2.0' : undefined;
        } else {
            properties.version = linkedService.version === driverVersions.v2 ? '2.0' : '1.0';
        }
        if (linkedService.version === driverVersions.v2) {
            this._toV2Resource(linkedService, properties);
        } else {
            this._toV1Resource(linkedService, properties);
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: AzurePostgreSqlLinkedService
    ): Promise<AzurePostgreSqlLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzurePostgreSqlLinkedService;

        linkedService.serverName = undefined;
        linkedService.port = undefined;
        linkedService.databaseName = undefined;
        linkedService.userName = undefined;
        linkedService.password = undefined;
        linkedService.encryptionMethod = EncryptionMethod.NoEncryption;
        linkedService.validateServerCertificate = false;

        linkedService.isOldResource = !properties.version;
        if (!!properties.version) {
            linkedService.version = properties.version === '2.0' ? driverVersions.v2 : driverVersions.v1;
        } else {
            linkedService.version = driverVersions.v1;
        }

        if (linkedService.version === driverVersions.v2) {
            this._toV2Model(properties, linkedService);
        } else {
            this._toV1Model(properties, linkedService);
        }

        return linkedService;
    }

    private _toV1Resource(linkedService: AzurePostgreSqlLinkedService,
        properties: Model.AzurePostgreSqlLinkedService) {
        if (!linkedService.enableAKV) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.add(
                'Host',
                connectionStringPartToResource(linkedService, AzurePostgreSqlLinkedService.SERVER_NAME)
            );
            builder.add('Port', connectionStringPartToResource(linkedService, AzurePostgreSqlLinkedService.PORT));
            builder.add(
                'Database',
                connectionStringPartToResource(linkedService, AzurePostgreSqlLinkedService.DATABASE_NAME)
            );
            builder.add('UID', connectionStringPartToResource(linkedService, AzurePostgreSqlLinkedService.USER_NAME));
            if (linkedService.password && !(linkedService.password instanceof AzureKeyVault)) {
                builder.add(
                    'PWD',
                    connectionStringPartToResource(linkedService, AzurePostgreSqlLinkedService.PASSWORD)
                );
            }
            builder.add(
                'EncryptionMethod',
                linkedService.encryptionMethod ? linkedService.encryptionMethod.toString() : '0'
            );
            if (linkedService.encryptionMethod !== EncryptionMethod.NoEncryption) {
                builder.add('ValidateServerCertificate', linkedService.validateServerCertificate ? '1' : '0');
            }
            if (linkedService.additionalFields) {
                linkedService.additionalFields.forEach((pair) => {
                    // v2 additional fields are not all string, v2 -> v1, we need to convert them to string
                    builder.add(pair.key, `${pair.value}` || '');
                });
            }
            const connectionString =
                linkedService.serverName === Constants.maskedCredential &&
                    linkedService.databaseName === Constants.maskedCredential &&
                    linkedService.userName === Constants.maskedCredential &&
                    linkedService.password === Constants.maskedCredential
                    ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    ({
                        type: 'SecureString',
                        value: Constants.maskedCredential
                    } as Model.SecureString)
                    : this._connectionStringToResource(builder.connectionString);
            properties.typeProperties = {
                connectionString,
                password:
                    linkedService.password instanceof AzureKeyVault
                        ? akvToResource(linkedService.password, this.context)
                        : undefined
            };
        } else {
            properties.typeProperties = {
                connectionString: akvToResource(linkedService.referedAKV, this.context)
            };
        }
    }

    private _toV2Resource(linkedService: AzurePostgreSqlLinkedService, properties: Model.AzurePostgreSqlLinkedService) {
        properties.typeProperties = {
            server: this.valueToResource(linkedService, AzurePostgreSqlLinkedService.SERVER_NAME),
            port: this.valueToResource(linkedService, AzurePostgreSqlLinkedService.PORT),
            database: this.valueToResource(linkedService, AzurePostgreSqlLinkedService.DATABASE_NAME),
            sslMode: this.valueToResource(linkedService, AzurePostgreSqlLinkedService.SSL_MODE),
            username: undefined,
            password: undefined
        };

        properties.typeProperties.authenticationType = linkedService.authenticationType;
        if (linkedService.authenticationType === azurePostgreSqlAuthTypes.ServicePrincipal) {
            if (linkedService.authReferenceMethod === azureDefaultAuthRefTypes.InlineRefTypeAuthentication) {
                properties.typeProperties = {
                    ...properties.typeProperties,
                    username: this.valueToResource(linkedService, AzurePostgreSqlLinkedService.USER_NAME),
                    tenant: this.valueToResource(linkedService, AzurePostgreSqlLinkedService.TENANT),
                    servicePrincipalId: this.valueToResource(linkedService, AzurePostgreSqlLinkedService.SERVICE_PRINCIPAL_ID),
                    azureCloudType: linkedService.azureCloudType,
                    servicePrincipalCredentialType: linkedService.servicePrincipalCredentialType,
                    servicePrincipalKey: linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalKey
                        ? parameterizablePasswordOrAKVtoSecretBase(linkedService, AzurePostgreSqlLinkedService.SERVICE_PRINCIPAL_KEY, this.context)
                        : undefined,
                    servicePrincipalEmbeddedCert: linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert ?
                        parameterizablePasswordOrAKVtoSecretBase(linkedService, AzurePostgreSqlLinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT, this.context)
                        : undefined,
                    servicePrincipalEmbeddedCertPassword: linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert ?
                        parameterizablePasswordOrAKVtoSecretBase(linkedService, AzurePostgreSqlLinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT_PASSWORD, this.context)
                        : undefined
                }
            } else {
                properties.typeProperties = {
                    ...properties.typeProperties,
                    credential: linkedService.credential
                        ? (ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference)
                        : undefined
                }
            }

        } else if (linkedService.authenticationType === azurePostgreSqlAuthTypes.Basic) {
            properties.typeProperties = {
                ...properties.typeProperties,
                username: this.valueToResource(linkedService, AzurePostgreSqlLinkedService.USER_NAME),
                password: parameterizablePasswordOrAKVtoSecretBase(linkedService, AzurePostgreSqlLinkedService.PASSWORD, this.context)
            }

        } else if (linkedService.authenticationType === azurePostgreSqlAuthTypes.UserAssignedManagedIdentity) {
            properties.typeProperties = {
                ...properties.typeProperties,
                credential: linkedService.credential
                    ? (ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference)
                    : undefined
            }

        }

        if (linkedService.parameterizableAdditionalFields?.length > 0) {
            properties.typeProperties = {
                ...properties.typeProperties,
                ...toParameterObject(linkedService.parameterizableAdditionalFields, false, this.valueToResource)
            };
        }
    }

    private _toV1Model(properties: Model.AzurePostgreSqlLinkedService, linkedService: AzurePostgreSqlLinkedService) {
        if (isString(properties.typeProperties.connectionString)) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.parse(properties.typeProperties.connectionString as string);

            connectionStringPartToModel(
                linkedService,
                AzurePostgreSqlLinkedService.SERVER_NAME,
                builder.getValue('Host', false) ||
                builder.getValue('Server', false) ||
                builder.getValue('HostName', false) ||
                ''
            );
            connectionStringPartToModel(
                linkedService,
                AzurePostgreSqlLinkedService.PORT,
                builder.getValue('Port', false) || builder.getValue('PortNumber', false) || undefined
            );
            connectionStringPartToModel(
                linkedService,
                AzurePostgreSqlLinkedService.DATABASE_NAME,
                builder.getValue('Database', false) || builder.getValue('DB', false) || ''
            );
            connectionStringPartToModel(
                linkedService,
                AzurePostgreSqlLinkedService.USER_NAME,
                builder.getValue('UID', false) || builder.getValue('LogonID', false) || ''
            );

            linkedService.encryptionMethod = convertToEncryptionMethod(
                builder.getValue('EncryptionMethod', false) || builder.getValue('EM', false)
            );
            linkedService.validateServerCertificate =
                linkedService.encryptionMethod !== EncryptionMethod.NoEncryption
                    ? convertToValidateServerCertificate(builder.getValue('ValidateServerCertificate', false))
                    : false;
            linkedService.password = Constants.maskedCredential;
            linkedService.additionalFields = builder.getRemainedKeyValuePairs([
                'Host',
                'Server',
                'HostName',
                'Port',
                'PortNumber',
                'Database',
                'DB',
                'UID',
                'LogonID',
                'EncryptionMethod',
                'EM',
                'ValidateServerCertificate',
                'PWD',
                'Password'
            ]);
            if (properties.typeProperties.password) {
                linkedService.password = secretBaseToPasswordOrAKV(properties.typeProperties.password as Model.SecretBase, false, this.context);
            }
        } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
            linkedService.serverName = Constants.maskedCredential;
            linkedService.databaseName = Constants.maskedCredential;
            linkedService.userName = Constants.maskedCredential;
            linkedService.password = Constants.maskedCredential;
            if (properties.typeProperties.password) {
                parameterizableSecretBaseToModel(
                    linkedService,
                    AzurePostgreSqlLinkedService.PASSWORD,
                    properties.typeProperties.password, this.context
                );
            }
        } else if (
            properties.typeProperties.connectionString &&
            (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret'
        ) {
            linkedService.referedAKV = akvToModel(
                properties.typeProperties.connectionString as Model.AzureKeyVaultSecretReference,
                this.context
            );
            linkedService.enableAKV = true;
        }
    }

    private _toV2Model(properties: Model.AzurePostgreSqlLinkedService, linkedService: AzurePostgreSqlLinkedService) {
        const typeProperties = properties.typeProperties as Model.AzurePostgreSqlLinkedServiceTypeProperties;
        ValueAdapter.toModel(linkedService, AzurePostgreSqlLinkedService.SERVER_NAME, typeProperties.server);
        ValueAdapter.toModel(linkedService, AzurePostgreSqlLinkedService.PORT, typeProperties.port);
        ValueAdapter.toModel(linkedService, AzurePostgreSqlLinkedService.DATABASE_NAME, typeProperties.database);
        ValueAdapter.toModel(linkedService, AzurePostgreSqlLinkedService.SSL_MODE, typeProperties.sslMode);

        linkedService.authenticationType = typeProperties.authenticationType || azurePostgreSqlAuthTypes.Basic;
        if (linkedService.authenticationType === azurePostgreSqlAuthTypes.ServicePrincipal) {
            ValueAdapter.toModel(linkedService, AzurePostgreSqlLinkedService.USER_NAME, typeProperties.username);
            if (typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential),
                    properties.typeProperties.credential, this.context);
                linkedService.authReferenceMethod = azureDefaultAuthRefTypes.CredentialRefTypeAuthentication;
            } else {
                linkedService.authReferenceMethod = azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
                linkedService.azureCloudType = typeProperties.azureCloudType;
                ValueAdapter.toModel(linkedService, AzurePostgreSqlLinkedService.TENANT, typeProperties.tenant);
                ValueAdapter.toModel(linkedService, AzurePostgreSqlLinkedService.SERVICE_PRINCIPAL_ID, typeProperties.servicePrincipalId);
                linkedService.servicePrincipalCredentialType = typeProperties.servicePrincipalCredentialType;
                if (typeProperties.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalKey) {
                    parameterizableSecretBaseToModel(linkedService, AzurePostgreSqlLinkedService.SERVICE_PRINCIPAL_KEY, typeProperties.servicePrincipalKey, this.context);
                } else if (typeProperties.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert) {
                    parameterizableSecretBaseToModel(linkedService, AzurePostgreSqlLinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT, typeProperties.servicePrincipalEmbeddedCert, this.context);
                    parameterizableSecretBaseToModel(linkedService, AzurePostgreSqlLinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT_PASSWORD, typeProperties.servicePrincipalEmbeddedCertPassword, this.context);
                }
            }

        } else if (linkedService.authenticationType === azurePostgreSqlAuthTypes.Basic) {
            ValueAdapter.toModel(linkedService, AzurePostgreSqlLinkedService.USER_NAME, typeProperties.username);
            parameterizableSecretBaseToModel(linkedService, AzurePostgreSqlLinkedService.PASSWORD, typeProperties.password, this.context);
        } else if (linkedService.authenticationType === azurePostgreSqlAuthTypes.UserAssignedManagedIdentity) {
            linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential),
                properties.typeProperties.credential, this.context);
        }


        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const additionalPropertiesObj: { [key: string]: any } = {};
        for (const propertyName in typeProperties) {
            if (azPgSqlAdditionalFieldsAllowedKeys.includes(propertyName as AzurePostGreSqlAdditionalPropertiesKeyType)) {
                additionalPropertiesObj[propertyName] =
                    typeProperties[propertyName as keyof Model.AzurePostgreSqlLinkedServiceTypeProperties];
            }
        }
        linkedService.parameterizableAdditionalFields = toParameterClass(
            additionalPropertiesObj,
            linkedService
        ) as Array<ParameterConfig<AzurePostGreSqlAdditionalPropertiesKeyType>>;
    }
}
